import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Clock,
  FileImage,
  FileVideo,
  Search,
  Filter,
  Calendar,
  BarChart3,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Eye,
  Download,
  Share2,
  CreditCard,
  ArrowRight,
  Users,
  Globe,
  FolderTree,
  RefreshCw,
  Edit2,
  Check,
  X,
  FolderOpen,
  Folder,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { RecentAnalysesService, RecentAnalysisItem } from '../lib/recent-analyses';
import { truncateAnalysisName } from '../lib/utils';
import { supabase } from '../lib/supabase';

export function RecentAnalysisPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analyses, setAnalyses] = useState<RecentAnalysisItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState({
    totalAnalyses: 0,
    averageScore: 0,
    creditsUsed: 0,
    creativeFamilies: 0
  });
  
  // Pagination constants
  const INITIAL_LOAD_SIZE = 20;
  const LOAD_MORE_SIZE = 10;
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [groupByFamily, setGroupByFamily] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  // Rename states
  const [editingAnalysisId, setEditingAnalysisId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [renamingLoading, setRenamingLoading] = useState(false);

  // Expansion states for grouped families
  const [expandedFamilies, setExpandedFamilies] = useState<Set<string>>(new Set());

  // Load data on component mount and when filters change
  useEffect(() => {
    loadAnalyses();
    loadStats();
  }, [searchTerm, groupByFamily, filterStatus, sortBy]);

  // Set up real-time subscription for new analyses
  useEffect(() => {
    const subscription = supabase
      .channel('analyses_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'analyses' }, 
        (payload) => {
          loadAnalyses();
          loadStats();
        }
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'analyses' }, 
        (payload) => {
          loadAnalyses();
          loadStats();
        }
      )
      .subscribe();

    // Listen for analysis completion events from other pages
    const handleAnalysisCompleted = (event: CustomEvent) => {
      // Refresh data when analysis completes
      loadAnalyses();
      loadStats();
    };

    window.addEventListener('analysisCompleted', handleAnalysisCompleted as EventListener);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('analysisCompleted', handleAnalysisCompleted as EventListener);
    };
  }, []);

  const loadAnalyses = async (reset = true) => {
    try {
      if (reset) {
        setLoading(true);
        setError(null);
      }
      
      const data = await RecentAnalysesService.getFilteredAnalyses({
        searchTerm: searchTerm || undefined,
        groupByFamily: groupByFamily,
        filterStatus: filterStatus === 'all' ? undefined : filterStatus,
        sortBy: sortBy as 'date' | 'score' | 'name',
        limit: INITIAL_LOAD_SIZE,
        offset: 0
      });
      
      setAnalyses(data);
      
      // Check if there are more analyses to load
      const hasMoreData = await RecentAnalysesService.hasMoreAnalyses({
        searchTerm: searchTerm || undefined,
        groupByFamily: groupByFamily,
        filterStatus: filterStatus === 'all' ? undefined : filterStatus,
        currentCount: data.length
      });
      
      setHasMore(hasMoreData);
      
    } catch (err) {
      console.error('Error loading analyses:', err);
      setError('Failed to load analyses');
    } finally {
      if (reset) {
        setLoading(false);
      }
    }
  };

  const loadMoreAnalyses = async () => {
    try {
      setLoadingMore(true);
      setError(null);
      
      const moreData = await RecentAnalysesService.getFilteredAnalyses({
        searchTerm: searchTerm || undefined,
        groupByFamily: groupByFamily,
        filterStatus: filterStatus === 'all' ? undefined : filterStatus,
        sortBy: sortBy as 'date' | 'score' | 'name',
        limit: LOAD_MORE_SIZE,
        offset: analyses.length
      });
      
      // Append new analyses to existing ones
      setAnalyses(prev => [...prev, ...moreData]);
      
      // Check if there are even more analyses to load
      const hasMoreData = await RecentAnalysesService.hasMoreAnalyses({
        searchTerm: searchTerm || undefined,
        groupByFamily: groupByFamily,
        filterStatus: filterStatus === 'all' ? undefined : filterStatus,
        currentCount: analyses.length + moreData.length
      });
      
      setHasMore(hasMoreData);
      
    } catch (err) {
      console.error('Error loading more analyses:', err);
      setError('Failed to load more analyses');
    } finally {
      setLoadingMore(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await RecentAnalysesService.getAnalysisStats();
      setStats(statsData);
    } catch (err) {
      console.error('Error loading stats:', err);
      // Don't show error for stats, just use defaults
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'rejected': return XCircle;
      default: return Clock;
    }
  };

  const getOverallStatus = (analysis: RecentAnalysisItem) => {
    return analysis.status;
  };

  const handleAnalysisClick = (analysis: RecentAnalysisItem) => {
    navigate(`/dashboard/analysis?id=${analysis.id}`);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Unknown date';
    }
  };

  const refreshData = () => {
    loadAnalyses();
    loadStats();
  };

  const toggleFamilyExpansion = (familyId: string) => {
    setExpandedFamilies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(familyId)) {
        newSet.delete(familyId);
      } else {
        newSet.add(familyId);
      }
      return newSet;
    });
  };

  const handleStartRename = (analysis: RecentAnalysisItem) => {
    setEditingAnalysisId(analysis.id);
    setEditingName(analysis.creativeName);
  };

  const handleCancelRename = () => {
    setEditingAnalysisId(null);
    setEditingName('');
    setRenamingLoading(false);
  };

  const handleSaveRename = async () => {
    if (!editingAnalysisId || !editingName.trim()) return;

    try {
      setRenamingLoading(true);
      const updatedAnalysis = await RecentAnalysesService.renameAnalysis(editingAnalysisId, editingName.trim());
      
      if (updatedAnalysis) {
        // Update the local analyses array
        setAnalyses(prevAnalyses => 
          prevAnalyses.map(analysis => 
            analysis.id === editingAnalysisId 
              ? { ...analysis, creativeName: updatedAnalysis.creativeName }
              : analysis
          )
        );
      }
      
      handleCancelRename();
    } catch (error) {
      console.error('Error renaming analysis:', error);
      setError('Failed to rename analysis');
    } finally {
      setRenamingLoading(false);
    }
  };

  const renderAnalysisItem = (analysis: RecentAnalysisItem, index: number, isSubItem: boolean = false) => {
    const overallStatus = getOverallStatus(analysis);
    const StatusIcon = getStatusIcon(overallStatus);
    const indentClass = isSubItem ? 'ml-4 sm:ml-8' : '';
    
    return (
      <motion.div
        key={analysis.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        className={`p-2 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer group ${indentClass} w-full overflow-hidden`}
        onClick={() => handleAnalysisClick(analysis)}
      >
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
          {/* Mobile: Show thumbnail with title and score */}
          <div className="sm:hidden flex items-center gap-2 w-full">
            <div className="relative flex-shrink-0">
              <img
                src={analysis.thumbnail}
                alt={analysis.creativeName}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div className="absolute -top-1 -right-1">
                {analysis.creativeType === 'video' ? (
                  <FileVideo className="w-4 h-4 text-purple-600" />
                ) : (
                  <FileImage className="w-4 h-4 text-blue-600" />
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              {editingAnalysisId === analysis.id ? (
                <div className="flex items-center gap-1 w-full">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 min-w-0 px-1 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveRename();
                      if (e.key === 'Escape') handleCancelRename();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveRename();
                    }}
                    disabled={renamingLoading || !editingName.trim()}
                    className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                  >
                    <Check className="w-3 h-3" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelRename();
                    }}
                    className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="font-semibold text-sm text-gray-900 group-hover:text-purple-600 transition-colors truncate" title={analysis.creativeName}>
                    {truncateAnalysisName.list(analysis.creativeName)}
                  </h3>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(overallStatus)}`}>
                      <StatusIcon className="w-3 h-3 inline mr-1" />
                      {overallStatus}
                    </span>
                    <span className="text-sm sm:text-base font-bold text-gray-900">{analysis.overallScore}%</span>
                  </div>
                </>
              )}
            </div>
          </div>
          
          {/* Desktop: Original thumbnail */}
          <div className="hidden sm:block relative flex-shrink-0">
            <img
              src={analysis.thumbnail}
              alt={analysis.creativeName}
              className="w-20 h-20 rounded-lg object-cover"
            />
            <div className="absolute -top-1 -right-1">
              {analysis.creativeType === 'video' ? (
                <FileVideo className="w-5 h-5 text-purple-600" />
              ) : (
                <FileImage className="w-5 h-5 text-blue-600" />
              )}
            </div>
          </div>
          
          {/* Analysis Info */}
          <div className="flex-1 min-w-0 w-full">
            {/* Desktop: Title and score in header */}
            <div className="hidden sm:flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-2">
              {editingAnalysisId === analysis.id ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 min-w-0 px-2 sm:px-3 py-1 text-sm sm:text-base border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveRename();
                      if (e.key === 'Escape') handleCancelRename();
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveRename();
                    }}
                    disabled={renamingLoading || !editingName.trim()}
                    className="p-1 text-green-600 hover:bg-green-100 rounded transition-colors disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelRename();
                    }}
                    className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base text-gray-900 group-hover:text-purple-600 transition-colors truncate" title={analysis.creativeName}>
                    {truncateAnalysisName.list(analysis.creativeName)}
                  </h3>
                  {analysis.versionCount && analysis.versionCount > 1 && (
                    <span className="inline-block mt-1 bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                      {analysis.versionCount} versions
                    </span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(overallStatus)}`}>
                  <StatusIcon className="w-3 h-3 inline mr-1" />
                  {overallStatus}
                </span>
                <span className="text-lg sm:text-2xl font-bold text-gray-900">{analysis.overallScore}%</span>
              </div>
            </div>
            
            {/* Mobile: Metadata in vertical layout */}
            <div className="sm:hidden space-y-1 mb-2">
              <div className="text-xs text-gray-600 truncate">
                Project: {analysis.project} • {formatDate(analysis.analysisDate)}
              </div>
              <div className="text-xs text-gray-600 truncate">
                Analyst: {analysis.analyst} • {analysis.creditsUsed} credit
              </div>
              {analysis.versionCount && analysis.versionCount > 1 && (
                <span className="inline-block bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded-full">
                  {analysis.versionCount} versions
                </span>
              )}
            </div>
            
            {/* Desktop: Original metadata layout */}
            <div className="hidden sm:flex flex-wrap gap-2 text-xs sm:text-sm text-gray-600 mb-3">
              <span className="min-w-0 flex items-center gap-1">
                <span className="text-xs">Project:</span>
                <span className="truncate max-w-[100px] sm:max-w-[150px]">{analysis.project}</span>
              </span>
              <span className="min-w-0 flex items-center gap-1">
                <span className="text-xs">Analyst:</span>
                <span className="truncate max-w-[100px] sm:max-w-[150px]">{analysis.analyst}</span>
              </span>
              <span className="whitespace-nowrap text-xs sm:text-sm">{formatDate(analysis.analysisDate)}</span>
              <div className="flex items-center gap-1 whitespace-nowrap text-xs sm:text-sm">
                <CreditCard className="w-3 h-3" />
                <span>{analysis.creditsUsed} credit</span>
              </div>
            </div>
            
            {/* Platform Scores - Mobile: 2 columns showing 4 items, Desktop: 6 columns */}
            {analysis.platforms && Object.keys(analysis.platforms).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-0.5 sm:gap-2 mb-1.5 sm:mb-3">
                {Object.entries(analysis.platforms).slice(0, 6).map(([platform, data]) => (
                  <div key={platform} className="text-center min-w-0">
                    <div className="text-[10px] sm:text-xs font-medium text-gray-700 capitalize truncate">{platform.slice(0, 4)}</div>
                    <div className={`text-[10px] sm:text-xs px-0.5 sm:px-1 py-0.5 rounded ${getStatusColor(data.status)}`}>
                      {data.score}%
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Issues - Show on mobile with limited items */}
            {analysis.mainIssues && analysis.mainIssues.length > 0 && (
              <div className="">
                <div className="text-xs text-gray-600 mb-1">Main Issues:</div>
                <div className="space-y-1 sm:flex sm:flex-wrap sm:gap-1 sm:space-y-0">
                  {analysis.mainIssues.slice(0, 2).map((issue, idx) => (
                    <span key={idx} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded block sm:inline-block truncate">
                      {issue}
                    </span>
                  ))}
                  {analysis.mainIssues.length > 2 && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      +{analysis.mainIssues.length - 2} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          {/* Mobile: Actions at bottom */}
          <div className="sm:hidden flex items-center justify-between border-t border-gray-100 pt-2 mt-2">
            <div className="flex items-center gap-2">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleStartRename(analysis);
                }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="Rename"
              >
                <Edit2 className="w-4 h-4 text-gray-600" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleAnalysisClick(analysis);
                }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                title="View"
              >
                <Eye className="w-4 h-4 text-gray-600" />
              </button>
            </div>
            <ArrowRight className="w-4 h-4 text-purple-600" />
          </div>
          
          {/* Desktop: Original actions */}
          <div className="hidden sm:flex items-center gap-1 sm:gap-2">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleStartRename(analysis);
              }}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Rename analysis"
            >
              <Edit2 className="w-4 h-4 text-gray-600" />
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleAnalysisClick(analysis);
              }}
              className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="View analysis"
            >
              <Eye className="w-4 h-4 text-gray-600" />
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                // Handle download action
              }}
              className="hidden sm:inline-flex p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Download report"
            >
              <Download className="w-4 h-4 text-gray-600" />
            </button>
            <button 
              onClick={(e) => {
                e.stopPropagation();
                // Handle share action
              }}
              className="hidden sm:inline-flex p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Share analysis"
            >
              <Share2 className="w-4 h-4 text-gray-600" />
            </button>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
              <ArrowRight className="w-4 h-4 text-purple-600" />
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full overflow-x-hidden">
      <Helmet>
        <title>Recent Analyses - Rulevia</title>
      </Helmet>
      <div className="px-1 py-3 sm:px-6 sm:py-6 max-w-7xl mx-auto">
        <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-2">Recent Analyses</h1>
              <p className="text-gray-600 text-sm sm:text-base">
                View and manage all your recent creative compliance analyses.
              </p>
            </div>
            <button
              onClick={refreshData}
              disabled={loading}
              className="flex items-center justify-center gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 text-xs sm:text-base flex-shrink-0 w-full sm:w-auto"
            >
              <RefreshCw className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </motion.div>
        </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-6 mb-4 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl p-3 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-10 sm:w-12 h-10 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-5 sm:w-6 h-5 sm:h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1">{stats.totalAnalyses}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Total Analyses</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl p-3 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-10 sm:w-12 h-10 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 sm:w-6 h-5 sm:h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1">{stats.averageScore}%</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Average Score</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl p-3 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-10 sm:w-12 h-10 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 sm:w-6 h-5 sm:h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1">{stats.creditsUsed}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Credits Used</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl p-3 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-2 sm:mb-4">
            <div className="w-10 sm:w-12 h-10 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <FolderTree className="w-5 sm:w-6 h-5 sm:h-6 text-yellow-600" />
            </div>
          </div>
          <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-1">{stats.creativeFamilies}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Creative Families</p>
        </motion.div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-white rounded-xl p-3 sm:p-6 border border-gray-200 mb-4 sm:mb-8"
      >
        <div className="flex flex-col gap-4">
          {/* Search Bar */}
          <div className="w-full relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search analyses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>
          
          {/* Filter Controls */}
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="flex flex-col sm:flex-row gap-3 flex-1">
              <button
                onClick={() => setGroupByFamily(!groupByFamily)}
                className={`flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 border rounded-lg transition-colors text-xs sm:text-base ${
                  groupByFamily 
                    ? 'bg-purple-50 border-purple-300 text-purple-700' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                } w-full sm:w-auto`}
              >
                {groupByFamily ? (
                  <FolderOpen className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                ) : (
                  <Folder className="w-3.5 sm:w-4 h-3.5 sm:h-4" />
                )}
                <span>{groupByFamily ? 'Grouped by Family' : 'Group by Family'}</span>
              </button>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full sm:w-auto px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
              >
                <option value="all">All Status</option>
                <option value="approved">Approved</option>
                <option value="warning">Warning</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            
            {/* Sort Control */}
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-xs sm:text-sm"
              >
                <option value="date">Date</option>
                <option value="score">Score</option>
                <option value="name">Name</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Analyses List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
        className="bg-white rounded-xl border border-gray-200"
      >
        <div className="p-3 sm:p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Analysis History</h2>
            {analyses.length > 0 && (
              <span className="text-sm text-gray-500">
                Showing {analyses.length} of {stats.totalAnalyses} analyses
              </span>
            )}
          </div>
        </div>
        
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
            <span className="ml-3 text-gray-600">Loading analyses...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={refreshData}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {!loading && !error && analyses.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">No analyses found</p>
            <p className="text-sm text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your filters or search terms'
                : 'Upload your first creative to get started'
              }
            </p>
          </div>
        )}

        {!loading && !error && analyses.length > 0 && (
          <div className="divide-y divide-gray-200">
            {analyses.map((analysis, index) => {
              const isGrouped = groupByFamily && analysis.versionCount && analysis.versionCount > 1;
              const familyId = analysis.familyName || analysis.creativeName;
              const isExpanded = expandedFamilies.has(familyId);

              return (
                <div key={analysis.id}>
                  {/* Main analysis item */}
                  <div className="relative">
                    {isGrouped && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFamilyExpansion(familyId);
                        }}
                        className="absolute left-0.5 sm:left-2 top-1/2 transform -translate-y-1/2 z-10 p-0.5 sm:p-1 hover:bg-gray-100 rounded transition-colors"
                        title={isExpanded ? 'Collapse versions' : 'Expand versions'}
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    )}
                    <div className={isGrouped ? 'pl-3 sm:pl-8' : ''}>
                      {renderAnalysisItem(analysis, index)}
                    </div>
                  </div>
                  
                  {/* Expanded versions */}
                  {isGrouped && isExpanded && analysis.versions && (
                    <div className="bg-gray-50 border-l-2 border-purple-200">
                      {analysis.versions.map((version, versionIndex) => (
                        <div key={version.id} className="border-b border-gray-200 last:border-b-0">
                          {renderAnalysisItem(version, versionIndex, true)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        
        {/* Load More Button */}
        {!loading && !error && analyses.length > 0 && hasMore && (
          <div className="p-3 sm:p-6 border-t border-gray-200 text-center">
            <button
              onClick={loadMoreAnalyses}
              disabled={loadingMore}
              className="px-3 sm:px-6 py-1.5 sm:py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2 mx-auto text-xs sm:text-base"
            >
              <RefreshCw className={`w-3.5 sm:w-4 h-3.5 sm:h-4 ${loadingMore ? 'animate-spin' : ''}`} />
              <span>
                {loadingMore ? 'Loading...' : `Load More (${LOAD_MORE_SIZE} more)`}
              </span>
            </button>
          </div>
        )}
      </motion.div>
      </div>
    </div>
  );
}